import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Typography,
  CircularProgress,
  SvgIcon,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack,
  Call,
  EmailRounded,
  CalendarTodayOutlined,
  LocationOnOutlined,
  CreditCard,
  FileDownloadOutlined,
  Check,
  East,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import {
  getUserOrderDetails,
  cancelUserOrder,
  userInvoiceDownload,
  completeOrderWithoutPayment,
} from "../../api/order-summary.api";
import { ReactComponent as NoImgIcon } from "../../assets/icons/no-img-icon.svg";
import { routes } from "../../constants/routes";

import "./order-details-page.scss";
import { AlertComponent } from "../../common/components";

const OrderDetailsPage = () => {
  const navigate = useNavigate();
  const params = useParams();

  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isNameOverflowing, setIsNameOverflowing] = useState(false);
  const nameRef = useRef<HTMLSpanElement>(null);

  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);

  const orderSummary = orderDetails?.summary;
  const partners = orderDetails?.partners || orderDetails?.providers;
  const hasPlanner = partners?.some((p: any) => !!p?.plannerId);

  const billingAddress = orderDetails?.billingAddress;
  const showPaidAmountTags = orderDetails?.displayPaidUnpaid === true;
  const apiStatus = orderDetails?.status ?? orderDetails?.orderStatus ?? "";
  const statusLabelMap: Record<string, string> = {
    paid: "Confirmed",
    confirmed: "Confirmed",
    pending: "Pending",
    completed: "Completed",
    cancelled: "Cancelled",
  };

  const statusLabel = statusLabelMap[apiStatus.toLowerCase()] || apiStatus;

  const formatDate = (date?: string | Date) => {
    if (!date) return "-";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const orderId = params?.id;
  const capitalizeFirst = (v?: string) =>
    v ? v.charAt(0).toUpperCase() + v.slice(1) : "-";

  const formatValue = (v?: string) => v ?? "-";

  const userMetaData = [
    {
      key: "Order Date",
      value: formatDate(orderDetails?.createdAt),
      icon: <CalendarTodayOutlined fontSize="small" />,
    },
    {
      key: "Location",
      value: `${formatValue(billingAddress?.city)}, ${formatValue(billingAddress?.state)}`,
      icon: <LocationOnOutlined fontSize="small" />,
    },
    {
      key: "Payment Method",
      value: capitalizeFirst(orderDetails?.paymentMethod),
      icon: <CreditCard fontSize="small" />,
    },
  ];

  const renderNeedType = (p: any) => {
    const hasPreNeed = (Number(p?.partnerPreNeedTypesTotalPrice) || 0) > 0;
    const hasAtNeed = (Number(p?.partnerAtNeedTypesTotalPrice) || 0) > 0;

    const labels = [];
    if (hasPreNeed) labels.push("Preneed");
    if (hasAtNeed) labels.push("Immediate Need");

    if (labels.length === 0) return null;

    return (
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center" }}
      >
        {labels.map((label, i) => (
          <React.Fragment key={label}>
            <Box
              component="span"
              sx={{
                color: "#104145",
                mx: 1,
                fontWeight: 900,
                fontSize: "18px",
                lineHeight: 1,
              }}
            >
              &bull;
            </Box>
            <Box
              component="span"
              sx={{
                color: "#666",
                fontSize: "14px",
                fontWeight: 400,
              }}
            >
              {label}
            </Box>
          </React.Fragment>
        ))}
      </Box>
    );
  };

  const orderPriceDetails = [
    { label: "Subtotal", value: `$${orderSummary?.subTotal ?? 0}.00` },
    { label: "Taxes", value: `$${orderSummary?.taxFee ?? 0}.00` },
    {
      label: "Discount",
      value: `-$${orderSummary?.discountAmount ?? 0}.00`,
      hide: (orderSummary?.discountAmount ?? 0) <= 0,
    },
  ];

  const orderTotalDetails = [
    {
      label: `Preneed Total${showPaidAmountTags ? " (Unpaid)" : ""}`,
      value: `$${orderSummary?.preNeedTotal ?? orderSummary?.PreneedTotal ?? 0}.00`,
    },
    {
      label: `Immediate Need Total${showPaidAmountTags ? " (Paid)" : ""}`,
      value: `$${orderSummary?.immediateNeedTotal ?? orderSummary?.ImmediateNeedTotal ?? 0}.00`,
    },
  ];

  const statusColorMap: any = {
    paid: { bg: "#dcfce7", text: "#008236" },
    confirmed: { bg: "#dcfce7", text: "#008236" },
    pending: { bg: "#FEF3C6", text: "#BB4D00" },
    cancelled: { bg: "#FFE2E2", text: "#C10007" },
    completed: { bg: "#EEF2FF", text: "#6366F1" },
  };
  const { bg, text } = statusColorMap[apiStatus.toLowerCase()] || {
    bg: "#E0E0E0",
    text: "#000000",
  };

  const fetchUserOrderDetails = async () => {
    try {
      setLoading(true);

      const response = await getUserOrderDetails(Number(orderId));

      if (response.data.success) {
        setOrderDetails(response.data?.data?.data);
        setUserDetails(response.data?.data?.data?.userDetails);
      }
    } catch (error) {
      console.error("ERROR FETCHING ORDER DETAILS::", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserOrderDetails();
  }, []);

  useEffect(() => {
    if (nameRef.current) {
      const { scrollWidth, clientWidth } = nameRef.current;
      setIsNameOverflowing(scrollWidth > clientWidth);
    }
  }, [userDetails, loading]);

  const handleBack = () => {
    navigate("/order-history");
  };

  const currentStatus = String(apiStatus || "").toLowerCase().trim();
  const canShowInvoice =
    orderDetails?.allowInvoiceDownload === true ||
    orderDetails?.allowInvoiceDownload == 1 ||
    String(orderDetails?.allowInvoiceDownload).toLowerCase().trim() ===
      "true" ||
    ["paid", "confirmed", "completed"].includes(currentStatus);

  const handleDownloadInvoice = async () => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await userInvoiceDownload(Number(orderId), timezone);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Invoice-${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      setAlertMessage("Invoice downloaded successfully");
      setAlertType("success");
      setAlertOpen(true);
    } catch (error) {
      console.error("ERROR DOWNLOADING INVOICE::", error);
      setAlertMessage("Failed to download invoice");
      setAlertType("error");
      setAlertOpen(true);
    }
  };

  const handleCancelOrder = async () => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await cancelUserOrder(Number(orderId), timezone);
      if (response.data.success) {
        setAlertMessage("Order cancelled successfully");
        setAlertType("success");
        setAlertOpen(true);
        fetchUserOrderDetails();
      }
    } catch (error) {
      console.error("ERROR CANCELLING ORDER::", error);
      setAlertMessage("Failed to cancel order");
      setAlertType("error");
      setAlertOpen(true);
    }
  };

  const handlePlaceOrder = async () => {
    const immediateNeedTotal =
      Number(orderSummary?.immediateNeedTotal) ||
      Number(orderSummary?.ImmediateNeedTotal) ||
      0;

    if (hasPlanner || immediateNeedTotal > 0) {
      navigate(routes.PAYMENT_PAGE, {
        state: {
          orderSummary: {
            ...orderSummary,
            orderId: Number(orderId),
            partnerList: partners,
          },
          partnerId: partners?.[0]?.partnerId || partners?.[0]?.plannerId,
          isFromCartPage: false,
          immediateNeedTotal: immediateNeedTotal,
          isFromOrderDetails: true,
        },
      });
    } else {
      try {
        const response = await completeOrderWithoutPayment(Number(orderId));
        if (response?.data?.success) {
          navigate("/payment-status", {
            state: {
              paymentStatus: true,
              isOrderSuccess: true,
              details: {
                orderId: Number(orderId),
                grandTotal: 0,
                txnId: `ORD-${Date.now()}`,
                transactionDate: new Date(),
              },
            },
          });
        }
      } catch (error) {
        console.error("Error completing order:", error);
        setAlertMessage("Failed to complete order");
        setAlertType("error");
        setAlertOpen(true);
      }
    }
  };

  return (
    <Container className="order-details">
      {loading ? (
        <Box
          sx={{
            minHeight: "90vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress className="cart-page__circular-progress" />
        </Box>
      ) : (
        <>
          {/* Back Button */}
          <Box className="order-details__back" onClick={handleBack}>
            <ArrowBack className="order-details__back-icon" />
            <Typography className="order-details__back-text">
              Back to Order History
            </Typography>
          </Box>

          {/* User Details */}
          <Box className="order-details__user">
            <Box className="order-details__user-details">
              <Box className="order-details__user-header">
                <Typography className="order-details__user-title">
                  <Tooltip
                    title={
                      [userDetails?.first_name, userDetails?.last_name]
                        .filter((v) => v && v.trim())
                        .join(" ") || "-"
                    }
                    placement="top"
                    disableHoverListener={!isNameOverflowing}
                  >
                    <span className="order-details__user-name" ref={nameRef}>
                      {[userDetails?.first_name, userDetails?.last_name]
                        .filter((v) => v && v.trim())
                        .join(" ") || "-"}
                    </span>
                  </Tooltip>{" "}
                  - Order #{orderDetails?.orderId}
                </Typography>
                <Typography className="order-details__card-status">
                  <Chip
                    label={statusLabel}
                    size="small"
                    className="order-history-card__status"
                    sx={{
                      backgroundColor: bg,
                      color: text,
                      fontWeight: 500,
                      height: 24,
                    }}
                  />
                </Typography>
              </Box>

              <Box className="order-details__user-contact">
                <Call className="order-details__contact-icon" />
                <Typography className="order-details__contact-text">
                  <Typography>
                    {formatValue(userDetails?.mobileNumber)}
                  </Typography>
                </Typography>
                <span className="order-details__divider">|</span>
                <EmailRounded className="order-details__contact-icon" />
                <Typography className="order-details__contact-text">
                  <Typography>{formatValue(userDetails?.email)}</Typography>
                </Typography>
              </Box>
            </Box>

            {/* User meta data */}
            <Box className="order-details__user-meta-data">
              {userMetaData.map((item) => (
                <Box
                  key={item.key}
                  className="order-details__user-meta-content"
                >
                  <Box className="order-details__meta-icon">{item.icon}</Box>

                  <Box className="order-details__meta-text">
                    <Typography className="order-details__meta-key">
                      {item.key}
                    </Typography>
                    <Typography className="order-details__meta-value">
                      {item.value}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Provider & Billing/Order Summary */}
          <Box className="order-details__content">
            {/* Left */}
            <Box className="order-details__provider-container">
              {partners?.map((partner: any, index: number) => {
                const isPlanner = !!partner?.plannerId;
                const images =
                  (isPlanner ? partner.plannerImages : partner.partnerImages) ||
                  [];
                const displayImage = images.find(
                  (img: any) => img.mediaType === "image",
                )?.url;
                const name = isPlanner
                  ? partner.plannerName
                  : partner.partnerName;
                const location = isPlanner
                  ? partner.plannerLocation
                  : [
                      partner?.partnerFullAddress?.street_line_1,
                      partner?.partnerFullAddress?.city,
                      partner?.partnerFullAddress?.state,
                      partner?.partnerFullAddress?.country,
                      partner?.partnerFullAddress?.zipcode,
                    ]
                      .map((v) => v ?? "-")
                      .join(", ");
                const phone = isPlanner
                  ? partner.plannerPhone
                  : partner.partnerPhone;
                const itemsList = isPlanner
                  ? partner.packages
                  : partner.services;
                const partnerKey = isPlanner
                  ? (partner.plannerId ?? index)
                  : (partner.partnerId ?? index);

                return (
                  <Box
                    key={partnerKey}
                    className="order-details__provider-section"
                  >
                    <Box className="order-details__provider">
                      {displayImage ? (
                        <img
                          src={displayImage}
                          alt={name}
                          className="order-details__provider-img"
                        />
                      ) : (
                        <Box className="order-details__image-box">
                          <SvgIcon
                            component={NoImgIcon}
                            style={{ height: "48px", width: "48px" }}
                            inheritViewBox
                          />
                          <Typography sx={{ fontSize: "14px", color: "#777" }}>
                            No Image Available
                          </Typography>
                        </Box>
                      )}

                      <Box className="order-details__provider-details">
                        <Typography className="order-details__provider-name">
                          {name}
                          {renderNeedType(partner)}
                        </Typography>
                        <Typography className="order-details__provider-address">
                          {location}
                        </Typography>
                        <Typography className="order-details__provider-phone">
                          {phone}
                        </Typography>
                      </Box>
                    </Box>

                    <Box className="order-details__services">
                      {itemsList?.map((service: any, serviceIndex: number) => {
                        const itemKey =
                          service.serviceId ??
                          service.packageId ??
                          serviceIndex;
                        const title =
                          service.serviceName || service.packageName;

                        return (
                          <Box
                            key={itemKey}
                            sx={{
                              mb: 3,
                            }}
                          >
                            {/* Service/Package Title */}
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography
                                className="order-details__services-title"
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Check fontSize="small" sx={{ mr: "0.5rem" }} />
                                {title}
                                {service.needType && (
                                  <>
                                    <Box
                                      component="span"
                                      sx={{
                                        color: "#104145",
                                        mx: 1,
                                        fontWeight: 900,
                                        fontSize: "18px",
                                        lineHeight: 1,
                                      }}
                                    >
                                      &bull;
                                    </Box>
                                    <Box
                                      component="span"
                                      sx={{
                                        color: "#666",
                                        fontSize: "14px",
                                        fontWeight: 400,
                                      }}
                                    >
                                      {service.needType === "pre_need"
                                        ? "Preneed"
                                        : "Immediate Need"}
                                    </Box>
                                  </>
                                )}
                              </Typography>
                              {isPlanner && service.price !== undefined && (
                                <Typography
                                  className="order-details__service-price"
                                  sx={{ fontWeight: 600 }}
                                >
                                  ${service.price}
                                </Typography>
                              )}
                            </Box>

                            {isPlanner && service.description && (
                              <Typography
                                className="order-details__service-description"
                                sx={{ ml: 4, mt: 1, mb: 1 }}
                              >
                                {service.description}
                              </Typography>
                            )}

                            <Divider
                              className="order-details__services-divider"
                              sx={{ my: 1.5 }}
                            />

                            {/* Sub Services for Providers */}
                            {!isPlanner &&
                              service?.subServices?.map(
                                (subService: any, subIndex: number) => (
                                  <Box
                                    key={subService.orderItemId ?? subIndex}
                                    className="order-details__services-list-box"
                                  >
                                    <Box className="order-details__services-list">
                                      <Typography className="order-details__service-name">
                                        {subService.subServiceName}
                                      </Typography>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 3,
                                        }}
                                      >
                                        {subService.itemType ===
                                          "merchandise" &&
                                          (subService.quantity || 1) > 0 && (
                                            <Typography
                                              sx={{
                                                color: "#104145",
                                                fontWeight: 700,
                                                fontSize: "14px",
                                              }}
                                            >
                                              Quantity: {subService.quantity}
                                            </Typography>
                                          )}
                                        <Typography className="order-details__service-price">
                                          ${parseFloat(subService.price)}
                                        </Typography>
                                      </Box>
                                    </Box>

                                    {subService.subServiceDescription && (
                                      <Typography className="order-details__service-description">
                                        {subService.subServiceDescription}
                                      </Typography>
                                    )}
                                  </Box>
                                ),
                              )}

                            {/* Services Included for Planners */}
                            {isPlanner &&
                              service?.servicesIncluded?.map(
                                (includedItem: any, includedIndex: number) => (
                                  <Box
                                    key={includedIndex}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      ml: 4,
                                      mb: 1,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        color: "#104145",
                                        fontWeight: 700,
                                        mr: 1,
                                        fontSize: 14,
                                      }}
                                    >
                                      ✔
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: 14,
                                        fontWeight: 500,
                                        color: "#000",
                                      }}
                                    >
                                      {includedItem.serviceName ||
                                        includedItem.name}
                                    </Typography>
                                  </Box>
                                ),
                              )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Right */}
            <Box sx={{ flex: 1.2 }}>
              <Box
                className="order-details__billing-summary"
                sx={{ flex: "none !important", width: "100%" }}
              >
                <Box className="order-details__billing-address">
                  <Typography className="order-details__billing-address-title">
                    Billing Address
                  </Typography>

                  <Typography className="order-details__billing-name">
                    {[billingAddress?.first_name, billingAddress?.last_name]
                      .filter((v) => v && v.trim())
                      .join(" ") || "-"}
                  </Typography>

                  <Typography>
                    {[
                      billingAddress?.street_line_1,
                      billingAddress?.city,
                      billingAddress?.state,
                      billingAddress?.country,
                      billingAddress?.pincode,
                    ]
                      .map((v) => v ?? "-")
                      .join(", ")}
                  </Typography>
                </Box>
                <Box className="order-details__order-summary">
                  <Typography className="order-details__order-summary-title">
                    Order Summary
                  </Typography>

                  {orderPriceDetails
                    .filter((item) => !item.hide)
                    .map((item) => (
                      <Box
                        key={item.label}
                        className="order-details__order-prices"
                      >
                        <Typography>{item.label}</Typography>
                        <Typography className="order-details__price-value">
                          {item.value}
                        </Typography>
                      </Box>
                    ))}

                  {!hasPlanner && (
                    <>
                      <Divider className="order-details__order-divider" />

                      <Box className="order-details__row-total">
                        {orderTotalDetails.map((item) => (
                          <Box
                            key={item.label}
                            className="order-details__order-prices"
                            sx={{ mb: 1.5 }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#666",
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#000",
                              }}
                            >
                              {item.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}

                  <Divider className="order-details__order-divider" />

                  <Box
                    className="order-details__total-prices"
                    justifyContent="center"
                  >
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                      }}
                    >
                      Total*
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "20px",
                        fontWeight: 700,
                      }}
                    >
                      ${orderSummary?.grandTotal ?? 0}.00
                    </Typography>
                  </Box>

                  <Typography
                    className="order-summary__billing-info"
                    sx={{
                      mt: 2,
                      mb: 2,
                      fontSize: "12px",
                      color: "#666",
                      lineHeight: "18px",
                    }}
                  >
                    *Billing applies only to Immediate Need Services and Preneed
                    Planner on MemorialGo. Preneed Service payments will be
                    finalized with the Provider.
                  </Typography>

                  {currentStatus === "pending" &&
                    (hasPlanner ||
                      (Number(orderSummary?.immediateNeedTotal) ||
                        Number(orderSummary?.ImmediateNeedTotal) ||
                        0) > 0 ||
                      (Number(orderSummary?.preNeedTotal) ||
                        Number(orderSummary?.PreneedTotal) ||
                        0) > 0) && (
                      <Button
                        fullWidth
                        variant="contained"
                        className="order-details__place-order-btn"
                        onClick={handlePlaceOrder}
                        endIcon={<East />}
                        sx={{
                          mt: 1,
                          backgroundColor: "#ebd28d",
                          color: "#104145",
                          textTransform: "none",
                          fontSize: "14px",
                          fontWeight: 600,
                          borderRadius: "8px",
                          height: "44px",
                          boxShadow: "none",
                          "&:hover": {
                            backgroundColor: "#e2c579",
                            boxShadow: "none",
                          },
                        }}
                      >
                        {hasPlanner ||
                        (Number(orderSummary?.immediateNeedTotal) ||
                          Number(orderSummary?.ImmediateNeedTotal) ||
                          0) > 0
                          ? "Place Order"
                          : "Complete Order"}
                      </Button>
                    )}
                  {currentStatus === "pending" && (
                    <Button
                      fullWidth
                      variant="outlined"
                      className="order-details__cancel-order-btn"
                      onClick={handleCancelOrder}
                      sx={{
                        mt: 1.5,
                        backgroundColor: "transparent",
                        color: "#C10007",
                        borderColor: "#D32F2F",
                        textTransform: "none",
                        fontSize: "14px",
                        fontWeight: 600,
                        borderRadius: "8px",
                        height: "44px",
                        "&:hover": {
                          backgroundColor: "#FFF2F2",
                          borderColor: "#C10007",
                        },
                      }}
                    >
                      Cancel Order
                    </Button>
                  )}
                </Box>
              </Box>

              <Box
                className="order-details__action-buttons"
                sx={{ mt: 2, display: "flex", flexDirection: "column" }}
              >
                {canShowInvoice && (
                  <Button
                    fullWidth
                    variant="outlined"
                    className="order-details__invoice-btn"
                    startIcon={<FileDownloadOutlined />}
                    onClick={handleDownloadInvoice}
                  >
                    Download Invoice
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
          <AlertComponent
            open={alertOpen}
            type={alertType}
            message={alertMessage || ""}
            autoHideDuration={4000}
            onClose={() => setAlertOpen(false)}
          />
        </>
      )}
    </Container>
  );
};

export default OrderDetailsPage;
